import { darken, lighten } from "polished/lib/index";

const color = {
  aliceBlue: "#ECF3FC",
  azure: "#F3FCFF",
  black: "#000000",
  cornFlowerBlue: "#4A90E2",
  darkGray: "#AAAAAA",
  darkLavender: "#D0E2F7",
  darkOliveGreen: "#417505",
  darkOrange: "#FF8A17",
  darkSlateBlue: "#2D4C77",
  darkSlateGray: "#4B4D54",
  ghostWhite: "#F6FAFC",
  lavender: "#E1F0FC",
  lightGoldenRodyel: "#EBFFD3",
  mediumTurquoise: "#5BC0DE",
  oliveDrab: "#6EB918",
  powderBlue: "#C6D9EF",
  silver: "#BFC5CC",
  snow: "#FBFBFB",
  white: "#FFFFFF",
  whiteSmoke: "#EEEEEE"
};

const font = {
  color: color.darkSlateGray,
  family: "'Open Sans', Helvetica, Arial, Sans-Serif",
  letterSpacing: "normal",
  size: "14px",
  sizeLg: "16px",
  sizeSm: "13px",
  sizeXs: "12px",
  sizeXxs: "11px",
  sizeXxxs: "10px",
  sizeXxxxs: "8px",
  sizeXl: "18px",
  sizeXxl: "20px",
  weightBold: "700",
  weightNormal: "400",
  weightSemiBold: "600"
};

const media = {
  xsphone: 250,
  smphone: 320,
  mdphone: 360,
  lgphone: 414,
  smtablet: 600,
  mdtablet: 768,
  lgtablet: 800,
  smdesktop: 992,
  mddesktop: 1280,
  lgdesktop: 1600
};

const spacing = {
  none: "0",
  xxs: "1px",
  xs: "2px",
  sm: "5px",
  smm: "8px",
  md: "10px",
  lg: "15px",
  xl: "20px",
  xxl: "25px"
};

const theme = {
  background: {
    color: color.white
  },
  button: {
    borderWidth: "0px",
    borderStyle: "none"
  },
  primaryButton: {
    bgColor: color.cornFlowerBlue,
    textColor: color.white,
    borderColor: "none",
    boxShadow: `0 1px 4px 0 ${lighten(0.274, color.cornFlowerBlue)}`
  },
  primaryInverseButton: {
    bgColor: color.white,
    textColor: color.cornFlowerBlue,
    borderColor: "none",
    boxShadow: `0 1px 4px 0 ${lighten(0.274, color.cornFlowerBlue)}`
  },
  cashbackText: {
    textColor: color.oliveDrab
  },
  clickOutButton: {
    bgColor: color.darkOrange,
    borderColor: "none",
    borderRadius: "2px",
    borderStyle: "none",
    borderWidth: "0px",
    boxShadow: `0 1px 4px 0 ${lighten(0.274, color.darkOrange)}`,
    hoverBackgroundColor: `${darken(0.05, color.darkOrange)}`,
    hoverTextColor: color.white,
    textColor: color.white
  },
  color: color,
  filter: {
    collapseLinkColor: color.cornFlowerBlue,
    mainFiltersBackgroundColor: color.azure,
    mainFiltersBorderColor: color.powderBlue,
    moreFiltersColor: color.cornFlowerBlue,
    resetFiltersColor: color.cornFlowerBlue,
    secondaryFiltersBackgroundColor: color.white,
    secondaryFiltersBorderColor: color.whiteSmoke,
    selectAllLinkColor: color.cornFlowerBlue,
    checkboxList: {
      borderColor: color.cornFlowerBlue,
      checkedBackgroundColor: color.cornFlowerBlue,
      checkColor: color.white,
      hoverBackgroundColor: color.lavender
    },
    logoListFilter: {
      backgroundColor: color.white,
      borderColor: color.cornFlowerBlue,
      hoverBackgroundColor: color.aliceBlue
    },
    radioButtonList: {
      borderColor: color.cornFlowerBlue,
      checkedBackgroundColor: color.cornFlowerBlue,
      checkColor: color.white,
      hoverBackgroundColor: color.lavender
    },
    selectFilter: {
      textColor: color.cornFlowerBlue
    }
  },
  font: font,
  generic: {
    borderRadius: "2px",
    maxWidgetWidth: null,
    minWidgetWidth: null
  },
  media: media,
  modal: {
    backgroundColor: color.white,
    closeButtonsBackgroundColor: color.aliceBlue,
    tco: {
      shopsLinkColor: color.cornFlowerBlue
    },
    textColor: color.cornFlowerBlue
  },
  monthlyFee: {
    currencyColor: color.darkSlateGray
  },
  nonDefaultFilter: {
    resetFiltersColor: color.cornFlowerBlue,
    valueColor: color.cornFlowerBlue,
    valueBackgroundColor: color.lavender
  },
  noResults: {
    headerSize: font.sizeXxl
  },
  offerContent: {
    backgroundColor: color.ghostWhite
  },
  offerExtraText: {
    strongTextColor: color.oliveDrab,
    textColor: color.darkSlateGray
  },
  offerResults: {
    backgroundColor: color.white,
    blockBackgroundColor: color.snow,
    borderColor: color.darkGray,
    collapsibleLinkColor: color.cornFlowerBlue,
    collapsibleLinkFontSize: font.size,
    collapsibleLinkPositionTop: "0px",
    collapsibleLinkAlign: "right",
    shopsLinkColor: color.cornFlowerBlue,
    sponsoredBorderColor: color.darkOrange
  },
  offerTitle: {
    textColor: color.darkSlateGray,
    withImage: true
  },
  pagination: {
    activeNumberColor: color.darkGray,
    gapColor: color.darkGray,
    numbersColor: color.cornFlowerBlue,
    previousNextColor: color.cornFlowerBlue
  },
  phoneChanger: {
    phoneImageColor: color.aliceBlue,
    simOnlyImageColor: color.cornFlowerBlue,
    show: true
  },
  phoneInfoBar: {
    mobilePhoneVariantAndMemorySelectorBackgroundColor: color.aliceBlue,
    mobilePhoneVariantAndMemorySelectorCloseButtonBackgroundColor:
      color.lavender,
    mobilePhoneVariantAndMemorySelectorCloseButtonColor: color.cornFlowerBlue,
    openMobilePhoneVariantAndMemorySelectorColor: color.cornFlowerBlue,
    show: true
  },
  phonePicker: {
    brandsNavTextColor: color.cornFlowerBlue,
    brandTextColor: color.cornFlowerBlue,
    itemHoverColor: color.aliceBlue,
    tabsColor: color.cornFlowerBlue,
    tabsSearchColor: color.whiteSmoke
  },
  spacing: spacing,
  sponsoredPosition: {
    borderColor: color.darkOrange,
    labelBackgroundColor: color.darkOrange
  },
  slider: {
    backGroundColor: color.whiteSmoke,
    fillColor: color.cornFlowerBlue,
    handleColor: color.cornFlowerBlue,
    valueColor: color.cornFlowerBlue,
    valueBackgroundColor: color.lavender
  },
  tco: {
    currencyColor: color.darkSlateGray
  }
};

export default theme;
export { color, font, media, spacing };
